import { AbyssTheme as themeConfiguration } from '@src/app/browser';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { Alert } from '@uhg-abyss/web/ui/Alert';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { isEmpty } from 'lodash';
import React, { useMemo } from 'react';

import { CurrentActiveTagsTable } from './components/CurrentActiveTagsTable';
import { TagHistoryTable } from './components/TagHistoryTable';

/**
 * Widget: Tags
 *
 * Displays affiliated tags with the risk record.
 *
 * @param assets
 * @param riskRecord
 * @returns {Element}
 * @constructor
 */
export function Tags({ assets = {}, riskRecord = {} }) {
  const tags = assets?.ListTags?.data || [];
  const addedTags = riskRecord?.tagModificationDetail?.currentTagList || [];
  const removedTags = riskRecord?.tagModificationDetail?.removedTagList || [];

  const addedRows = useMemo(() => {
    return addedTags.map((addedTag) => {
      const items = addedTag?.tag?.split('-');
      const row = {
        category: items?.[0],
        description: '',
        lastModified: addedTag?.lastAddedDate,
        name: items?.[1],
        status: 'Added',
      };
      const matchedTag = tags?.tagsList?.[row.category]?.find((tag) => {
        return tag?.categoryCode === row.category && tag?.code === row.name;
      });
      if (matchedTag) {
        row.description = matchedTag?.categoryDesc;
      }
      return row;
    });
  }, [addedTags, tags]);

  const allRows = useMemo(() => {
    const removedRows = removedTags.map((removedTag) => {
      const items = removedTag?.tag?.split('-');
      const row = {
        category: items?.[0],
        description: '',
        lastModified: removedTag?.lastRemovedDate,
        name: items?.[1],
        status: 'Removed',
      };
      const matchedTag = tags?.tagsList?.[row.category]?.find((tag) => {
        return tag?.categoryCode === row.category && tag?.code === row.name;
      });
      if (matchedTag) {
        row.description = matchedTag?.categoryDesc;
      }
      return row;
    });
    return [...addedRows, ...removedRows];
  }, [addedRows, removedTags, tags]);

  return (
    <ErrorHandler location="src/widgets/RiskRecords/Tags/Tags.jsx">
      {isEmpty(allRows) ? (
        <Alert title="There are no tags for this risk record." variant="info" />
      ) : (
        <Layout.Stack
          alignItems="left"
          alignLayout="left"
          grow
          space={Number(themeConfiguration?.theme?.space?.lg) || 24}
        >
          <CurrentActiveTagsTable rows={addedRows} />
          <TagHistoryTable rows={allRows} />
        </Layout.Stack>
      )}
    </ErrorHandler>
  );
}
